if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.bindOptionLinks();
      }

      bindOptionLinks(root = this) {
        root.querySelectorAll('a[data-value]').forEach((item) => {
          if (item.dataset.localizationBound === 'true') return;
          item.dataset.localizationBound = 'true';
          item.addEventListener('click', this.onItemClick.bind(this));
        });
      }

      ensureCountryOptions() {
        const dataElement = this.querySelector('script[data-country-options]');
        const target = this.querySelector('[data-country-options-target]');
        if (!dataElement || !target || target.dataset.hydrated === 'true') return;

        try {
          const data = JSON.parse(dataElement.textContent);
          const fragment = document.createDocumentFragment();

          if (data.showPopularCountries && data.popularCountries.length > 1) {
            fragment.appendChild(
              this.createCountryList(data.popularCountries, {
                popular: true,
                label: data.popularLabel,
                currentIsoCode: data.currentIsoCode,
                targetId: target.id,
              })
            );
          }

          fragment.appendChild(
            this.createCountryList(data.countries, {
              popular: false,
              currentIsoCode: data.currentIsoCode,
              targetId: target.id,
            })
          );

          target.appendChild(fragment);
          target.dataset.hydrated = 'true';
          dataElement.remove();
          this.bindOptionLinks(target);
        } catch (error) {
          target.dataset.hydrated = 'error';
        }
      }

      createCountryList(countries, { popular, label, currentIsoCode, targetId }) {
        const list = document.createElement('ul');
        list.setAttribute('role', 'list');
        list.className = popular ? 'list-unstyled popular-countries' : 'list-unstyled countries';
        if (popular && label) list.setAttribute('aria-label', label);

        countries.forEach((country) => {
          const item = document.createElement('li');
          item.className = 'disclosure__item';
          item.tabIndex = -1;

          const link = document.createElement('a');
          link.className = 'link link--text disclosure__link caption-large focus-inset flex items-center gap-x-4';
          link.href = '#';
          link.dataset.value = country.isoCode;
          link.id = `${targetId}-${country.isoCode}${popular ? '-popular' : ''}`;
          if (country.isoCode === currentIsoCode) link.setAttribute('aria-current', 'true');

          const flag = document.createElement('img');
          flag.src = country.imageUrl;
          flag.alt = '';
          flag.loading = 'lazy';
          flag.decoding = 'async';
          flag.width = 20;
          flag.height = 20;
          flag.className = 'localization-forms__flag rounded-full size-7 object-cover object-center';
          link.appendChild(flag);

          const countryText = document.createElement('span');
          if (popular) {
            countryText.className = 'country';
            countryText.textContent = `${country.name} (${country.currencyIsoCode} ${country.currencySymbol})`;
          } else {
            countryText.className = 'country flex-1 flex justify-between';
            countryText.appendChild(document.createTextNode(country.name));
            const currency = document.createElement('span');
            currency.textContent = `${country.currencyIsoCode} (${country.currencySymbol})`;
            countryText.appendChild(currency);
          }

          link.appendChild(countryText);
          item.appendChild(link);
          list.appendChild(item);
        });

        return list;
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer').classList.remove('country-selector-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        this.ensureCountryOptions();
        const focusableItems = Array.from(this.querySelectorAll('a[data-value]')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;
        if (!focusableItems.length) return;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        this.ensureCountryOptions();
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer').classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      filterCountries() {
        this.ensureCountryOptions();
        const searchValue = this.elements.search.value.toLowerCase();
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a[data-value]');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = item.querySelector('.country').textContent.toLowerCase();
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries += 1;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
